import React, { useCallback, useEffect, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import {
  FormControl,
  TextField,
  Grid,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import {
  LocalizationProvider,
  DatePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt';
import * as Yup from 'yup';
import Progress from '../../components/Progress';
import { useDispatch, useSelector } from 'react-redux';
import { setDisable } from '../../redux/disable';
import { setLoading } from '../../redux/loading';
import Success from '../../components/Snack/Success';
import Error from '../../components/Snack/Error';
import { useStyles } from '../../components/Styles';
import GroupButton from '../../components/Buttons/ButtonGroup';
import NumberFormat from 'react-number-format';
import { format, isValid, parse } from 'date-fns';
import styleGlobal from '../../Global.module.css';
import InfoIcon from '@mui/icons-material/Info';
import SelectFieldContext from 'components/CustomInputsInFormikContext/Select';
import { formatValueInBrazilianCurrency } from 'utils';

function Transaction() {
  const location = useLocation();
  const [locale, setLocale] = React.useState('pt');
  const [openProgress, setOpenProgress] = useState(false);
  const nameProperty = useSelector((state) => state.nameProperty.nameProperty);
  const [selectedParent, setSelectedParent] = useState(null);
  const [showCod, setShowCod] = useState(false);
  const [showParent, setShowParent] = useState(true);
  const [bankAccounts, setBankAccounts] = useState(null);
  const [accountValue, setAccountValue] = useState(0);
  const [date, setDate] = useState(new Date());
  const s = '\xa0\xa0';
  const disable = useSelector((state) => state.disable.disable);
  const error = Error();
  const success = Success();
  const history = useHistory();
  const dispatch = useDispatch();
  let { id } = useParams();
  const classes = useStyles();

  useEffect(() => {
    if (location.state !== undefined) {
      if (location.state.customData) {
        setData(location.state.customData);
      } else if (location.state.data) {
        id = location.state.id;
        setData(location.state.data);
      }
    }
  }, [location]);

  function setData(data) {
    formik.setValues(data);
    setDate(parse(data.date, 'yyyy-MM-dd', new Date()));
    setSelectedParent(data.parent);
    setShowCod(true);
    setShowParent(!!data.parent);
  }

  async function loadData() {
    try {
      const response = await api.get('/bank-account');
      if (response.data.length === 0) {
        setOpenProgress(true);
      }
      if (response.data) {
        setBankAccounts(response.data);
      }
    } catch (err) {

    }
  }

  // retona os valores do form pelo id
  const getTransaction = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/transaction/${id}`);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  }, []);

  useEffect(() => {
    loadData();
    if (id) {
      getTransaction(id).then((data) => {
        const calculateBillValue = calcNewAccountValue(
          data.origin,
          data.destiny,
          data.quantity,
          data.type,
        );

        setAccountValue(calculateBillValue);

        formik.setValues({
          ...data,
        });
      });
    }
  }, [getTransaction, id, nameProperty]);

  const formik = useFormik({
    initialValues: {
      description: '',
      origin: null,
      destiny: null,
      date,
      quantity: 0,
      type: 'Depósito',
      source: '',
    },

    validationSchema: Yup.object({
      description: Yup.string().max(
        100,
        'Máximo de caracteres ultrapassado (100 caracteres).',
      ),
      origin: Yup.mixed().when('type', {
        is: (val) => !(val === 'Saque' || val === 'Transferência'),
        then: Yup.mixed().notRequired(),
        otherwise: Yup.mixed().required('Campo Obrigatório'),
      }),
      destiny: Yup.mixed().when('type', {
        is: (val) => !(val === 'Depósito' || val === 'Transferência'),
        then: Yup.mixed().notRequired(),
        otherwise: Yup.mixed().required('Campo Obrigatório'),
      }),
      date: Yup.date()
        .required('Campo Obrigatório')
        .typeError('A data deve ter o formato dd/mm/yyyy'),
      quantity: Yup.number().when(['type', 'origin'], {
        is: (type, origin) => (type === 'Saque' || type === 'Transferência') && origin !== null,
        then: Yup.number()
          .required('Campo Obrigatório')
          .positive('O Valor deve ser Positivo')
          .max(accountValue, `O valor maxímo permitido é ${formatValueInBrazilianCurrency(accountValue)}.`),
        otherwise: Yup.number()
          .required('Campo Obrigatório')
          .positive('O Valor deve ser Positivo'),
      }),
    }),

    onSubmit: (values) => {
      dispatch(setDisable(true));
      dispatch(setLoading(true));
      request(values);
    },
  });

  const isManualTransaction = formik.values.source.includes('Manual');

  async function request(data) {
    // envia os dados para api salvar no banco
    try {
      id
        ? await api.put(`/transaction/${id}`, { ...data, origin: data.origin?.id, destiny: data.destiny?.id })
        : await api.post('/transaction', { ...data });
      success();
      history.push('/transactions');
    } catch (err) {
      error(err.response.data.message);
    }
    dispatch(setDisable(false));
    dispatch(setLoading(false));
  }

  /**
   * return new value of account after submit.
   * @param {object} origin
   * @param {object} destiny
   * @param {number} movementValue
   * @param {string} type
   * @return {number}
   */
  function calcNewAccountValue(origin, destiny, movementValue, type) {
    if (type === 'Saque' && origin !== null) {
      return parseFloat(origin.value - movementValue);
    }
    if ((type === 'Depósito' || 'Transferência') && destiny !== null) {
      return parseFloat(destiny.value + movementValue);
    }
  }

  // Estilização do tooltip
  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: '#FFFFFF',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 300,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #0367bf',
    },
  }))(Tooltip);

  return (
    <header className={styleGlobal.headerGlobal}>
      {openProgress ? <Progress /> : null}
      <Card style={{ padding: '2rem', borderRadius: '20px' }}>
        <form onSubmit={formik.handleSubmit}>
          <div className={classes.root}>
            <h2 style={{ marginBottom: '2rem' }}>
              Transação
              {' '}
              <HtmlTooltip
                title={(
                  <>
                    Nessa tela é possível realizar o controle de todas as transações realizadas.
                  </>
                )}
                placement="right"
                arrow
                interactive
              >
                <InfoIcon color="primary" fontSize="inherit" />
              </HtmlTooltip>
            </h2>
            <CardContent style={{ padding: '0rem' }}>
              <FormGroup style={{ marginLeft: 0, marginRight: 0 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12}>
                    <FormGroup>
                      <TextField
                        id="description"
                        name="description"
                        value={formik.values.description}
                        label={<div>Descrição (Opcional)</div>}
                        variant="outlined"
                        onKeyDown={formik.handleBlur}
                        error={
                          formik.touched.description
                          && formik.errors.description
                        }
                        helperText={
                          formik.touched.description
                          && formik.errors.description
                        }
                        onChange={(e) => {
                          formik.setFieldValue('description', e.target.value);
                        }}
                        color="primary"
                      />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={locale}
                    >
                      <DatePicker
                        InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                        label="Data"
                        name="Date"
                        variant="inline"
                        inputVariant="outlined"
                        invalidDateMessage="Data Inválida"
                        value={date}
                        onChange={(d) => {
                          setDate(d);
                          formik.setFieldValue(
                            'date',
                            isValid(d) ? format(d, 'yyyy-MM-dd') : d,
                          );
                        }}
                        format="dd/MM/yyyy"
                        fullWidth
                        onBlur={formik.handleBlur}
                        error={formik.touched.date && formik.errors.date}
                        helperText={formik.touched.date && formik.errors.date}
                        disabled={id && !isManualTransaction}
                        // inputFormat="DD/MM/YYYY"
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={2.4}>
                    <FormikProvider value={formik}>
                      <SelectFieldContext
                        name="type"
                        disabled={id && !isManualTransaction}
                        id="type"
                        label="Tipo de Movimentação"
                        onChange={(e) => {
                          formik.setFieldValue('type', e.target.value);

                          if (e.target.value === 'Saque') {
                            formik.setFieldValue('destiny', null);
                          }
                          if (e.target.value === 'Depósito') {
                            formik.setFieldValue('origin', null);
                            setAccountValue(0);
                          }
                        }}
                      >
                        <MenuItem key={1} value="Depósito">Depósito</MenuItem>
                        <MenuItem key={2} value="Saque">Saque</MenuItem>
                        <MenuItem key={3} value="Transferência">Transferência</MenuItem>
                        <MenuItem key={4} disabled value="pagamento">Pagamento</MenuItem>
                        <MenuItem key={5} disabled value="recebimento">Recebimento</MenuItem>
                      </SelectFieldContext>
                    </FormikProvider>
                  </Grid>

                  <Grid item xs={12} sm={2.4}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="origin">Conta de Origem</InputLabel>
                      <Select
                        label="Conta de origem"
                        fullWidth
                        value={
                          formik.values.origin === null
                            ? ''
                            : String(formik.values.origin?.name)
                        }
                        onBlur={formik.handleBlur}
                        error={formik.touched.origin && formik.errors.origin}
                        helperText={
                          formik.touched.origin && formik.errors.origin
                        }
                        onChange={(e) => {
                          const account = bankAccounts.find(
                            (account) => account.name === e.target.value,
                          );
                          formik.setFieldValue('origin', account);
                          setAccountValue(account.value);
                        }}
                        disabled={(formik.values.type === 'Depósito' || id) && !isManualTransaction}
                      >
                        {bankAccounts?.map((account) => (
                          <MenuItem key={account.id} value={account.name}>
                            {account.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="destiny">Conta de Destino</InputLabel>
                      <Select
                        label="Conta de Destino"
                        fullWidth
                        value={
                          formik.values.destiny === null
                            ? ''
                            : String(formik.values.destiny?.name)
                        }
                        onBlur={formik.handleBlur}
                        error={formik.touched.destiny && formik.errors.destiny}
                        helperText={
                          formik.touched.destiny && formik.errors.destiny
                        }
                        onChange={(e) => {
                          const account = bankAccounts.find(
                            (account) => account.name === e.target.value,
                          );
                          formik.setFieldValue('destiny', account);
                          if (formik.values.type !== 'Transferência') {
                            setAccountValue(account.value);
                          }
                        }}
                        disabled={(formik.values.type === 'Saque' || id) && !isManualTransaction}
                      >
                        {bankAccounts
                          ?.filter((account) => account.name !== formik.values.origin?.name)
                          .map((account) => (
                            <MenuItem key={account.id} value={account.name}>
                              {account.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={2.4}>
                    <NumberFormat
                      inputProps={{ maxLength: 14 }}
                      InputLabelProps={{
                        style: { pointerEvents: 'auto' },
                        shrink: true,
                      }}
                      fullWidth
                      id="quantity"
                      label="Valor da Movimentação"
                      variant="outlined"
                      name="quantity"
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      prefix="R$ "
                      value={formik.values.quantity}
                      onValueChange={(values) => {
                        formik.values.quantity = values.floatValue;
                      }}
                      onBlur={formik.handleBlur}
                      customInput={TextField}
                      error={formik.touched.quantity && formik.errors.quantity}
                      helperText={
                        formik.touched.quantity && formik.errors.quantity
                      }
                      disabled={id && !isManualTransaction}
                    />
                  </Grid>

                  {id ? (
                    ''
                  ) : (
                    <Grid item xs={12} sm={2.4}>
                      <NumberFormat
                        InputLabelProps={{
                          style: { pointerEvents: 'auto' },
                          shrink: true,
                        }}
                        fullWidth
                        id="value"
                        label={
                          `Saldo Atual da Conta${formik.values.type === 'Transferência'
                            ? ' de Origem'
                            : ''}`
                        }
                        variant="outlined"
                        name="value"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="R$ "
                        value={accountValue}
                        onBlur={formik.handleBlur}
                        customInput={TextField}
                        disabled
                      />
                    </Grid>
                  )}

                  {id ? (
                    ''
                  ) : (
                    <Grid item xs={12} sm={2.4}>
                      <NumberFormat
                        InputLabelProps={{
                          style: { pointerEvents: 'auto' },
                          shrink: true,
                        }}
                        fullWidth
                        id="value"
                        label={
                          `Saldo Após a Movimentação${formik.values.type === 'Transferência'
                            ? ' no Destino'
                            : ''}`
                        }
                        variant="outlined"
                        name="value"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="R$ "
                        value={calcNewAccountValue(
                          formik.values.origin,
                          formik.values.destiny,
                          formik.values.quantity,
                          formik.values.type,
                        )}
                        onBlur={formik.handleBlur}
                        customInput={TextField}
                        disabled
                      />
                    </Grid>
                  )}
                </Grid>
              </FormGroup>
            </CardContent>
          </div>

          <GroupButton
            deleteRoute="/transaction/"
            routeDelete="/transaction"
            returnRoute="/transaction"
            id={id}
            disable={disable}
            message="A exclusão pode levar a exclusão de objetos que dependam dela. Tem certeza que deseja excluir?"
          />
        </form>
      </Card>
    </header>
  );
}

export default Transaction;
